import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { apiGet, apiPost } from "@/lib/api-client";

interface OpenPosition {
  id: number;
  market_id: string;
  direction: string;
  entry_price: number;
  entry_size: number;
  status: string;
  unrealized_pnl?: number | null;
}

function PositionRow({
  position,
  onClose,
  pendingId,
}: {
  position: OpenPosition;
  onClose: (id: number) => void;
  pendingId: number | undefined;
}) {
  const pnl = position.unrealized_pnl ?? 0;
  const isProfit = pnl >= 0;
  // All Close buttons are disabled while ANY close is in flight — a
  // double-click must not fire a second POST /api/paper/close.
  const closing = pendingId !== undefined;
  const closingThis = pendingId === position.id;

  return (
    <tr className="border-b border-white/5 hover:bg-white/5">
      <td className="max-w-[160px] truncate px-3 py-2 text-xs font-mono text-white/70" title={position.market_id}>
        {position.market_id}
      </td>
      <td className="px-3 py-2 text-xs">
        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${position.direction === "YES" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
          {position.direction}
        </span>
      </td>
      <td className="px-3 py-2 text-right text-xs">${position.entry_price.toFixed(3)}</td>
      <td className="px-3 py-2 text-right text-xs">${position.entry_size.toFixed(2)}</td>
      <td className="px-3 py-2 text-right text-xs">
        <span className={isProfit ? "text-green-400" : "text-red-400"}>
          {`${isProfit ? "+" : ""}$${pnl.toFixed(2)}`}
        </span>
      </td>
      <td className="px-3 py-2 text-right text-xs">
        {position.status === "open" ? (
          <button
            type="button"
            onClick={() => onClose(position.id)}
            disabled={closing}
            className="rounded border border-red-500/40 px-2 py-0.5 text-xs text-red-400 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {closingThis ? "Closing..." : "Close"}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export function PositionsTable() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["paper-positions-open"],
    queryFn: () => apiGet<{ positions: OpenPosition[] }>("/api/paper/positions?status=open"),
    refetchInterval: 30_000,
  });
  const closePosition = useMutation({
    mutationFn: (positionId: number) => apiPost("/api/paper/close", { position_id: positionId, reason: "manual" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["paper-positions-open"] });
      await queryClient.invalidateQueries({ queryKey: ["paper-portfolio-card"] });
    },
  });

  if (isLoading) return <LoadingState text="Loading positions..." />;

  if (isError) {
    return (
      <Panel title="Paper Open Positions">
        <QueryErrorState what="paper positions list" onRetry={() => void refetch()} />
      </Panel>
    );
  }

  const positions = data?.positions ?? [];

  return (
    <Panel title={`Paper Open Positions (${positions.length})`}>
      {closePosition.isError ? (
        <p role="alert" className="mb-2 text-xs text-amber-300">
          Close failed — the position is still open. Retry, or check backend status.
        </p>
      ) : null}
      {positions.length === 0 ? (
        <p className="py-4 text-center text-sm text-white/40">No open positions</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs text-white/40">
                <th className="px-3 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-left">Side</th>
                <th className="px-3 py-2 text-right">Entry</th>
                <th className="px-3 py-2 text-right">Size</th>
                <th className="px-3 py-2 text-right">PnL</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position) => (
                <PositionRow
                  key={position.id}
                  position={position}
                  onClose={(id) => closePosition.mutate(id)}
                  pendingId={closePosition.isPending ? closePosition.variables : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
